import { vec3 } from 'gl-matrix';
import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { dvfExtent } from '~/domain/dvf/dvfField';
import { paneImageOf } from '~/engine/view/paneImage';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { usePlaneRedrawTick } from '~/hooks/viewer/annotationOverlay';
import { useBodyOutline } from '~/hooks/viewer/useBodyOutline';
import { useDeformationUncertainty } from '~/hooks/viewer/useDeformationUncertainty';
import { deformationUncertaintyEnabledState } from '~/state/atoms/deformationDisplayAtoms';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import {
    clearPlaneCanvas,
    PLANE_OVERLAY_CANVAS_STYLE,
    PLANE_OVERLAY_REDRAW_EVENTS,
    planeLattice,
} from './planeCanvas';
import { uncertaintyCells } from './uncertaintyCells';

export default function DeformationUncertaintyOverlay({
    view,
}: {
    view: PlaneView | null;
}) {
    const enabled = useRecoilValue(deformationUncertaintyEnabledState);
    const uncertainty = useDeformationUncertainty();
    // The same body the arrows are confined to.
    const { insideBody, basis } = useBodyOutline(
        view?.environment.image?.parent,
        enabled && !!uncertainty
    );
    const viewManager = useRecoilValue(viewManagerState);
    const tick = usePlaneRedrawTick(viewManager, PLANE_OVERLAY_REDRAW_EVENTS);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const ctx = clearPlaneCanvas(canvasRef.current);
        if (
            !ctx ||
            !view ||
            !enabled ||
            !uncertainty ||
            paneImageOf(view).kind !== 'primary' ||
            view.environment.image?.parent.frameOfReferenceUid !==
                uncertainty.frameOfReferenceUid ||
            basis === 'computing'
        ) {
            return;
        }
        const { geometry, values, maxMm } = uncertainty;
        const pitchMm = Math.max(...geometry.spacing);
        const lattice = planeLattice(
            view,
            geometry,
            pitchMm,
            dvfExtent(geometry)
        );
        const halfU = vec3.scale(
            vec3.create(),
            view.viewXDirection,
            pitchMm / 2
        );
        const halfV = vec3.scale(
            vec3.create(),
            view.viewYDirection,
            pitchMm / 2
        );
        const corner = vec3.create();
        for (const cell of uncertaintyCells(
            lattice.points,
            geometry,
            values,
            maxMm
        )) {
            const centre = lattice.points[cell.index];
            if (insideBody && !insideBody(centre)) {
                continue;
            }
            ctx.fillStyle = cell.fill;
            ctx.beginPath();
            [
                [-1, -1],
                [1, -1],
                [1, 1],
                [-1, 1],
            ].forEach(([su, sv], k) => {
                vec3.scaleAndAdd(corner, centre, halfU, su);
                vec3.scaleAndAdd(corner, corner, halfV, sv);
                const [x, y] = view.canvasCoordsFromPatientCoords(corner);
                if (k === 0) {
                    ctx.moveTo(x, y);
                } else {
                    ctx.lineTo(x, y);
                }
            });
            ctx.closePath();
            ctx.fill();
        }
    }, [tick, view, enabled, uncertainty, insideBody, basis]);

    if (!enabled || !uncertainty) {
        return null;
    }
    return (
        <canvas
            ref={canvasRef}
            style={{ ...PLANE_OVERLAY_CANVAS_STYLE, zIndex: 5 }}
            data-testid="deformation-uncertainty-overlay"
        />
    );
}
